import { Search, Users, FileText, Loader2 } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { searchEmployeesByQuery } from "@/services/employeeService";

interface SearchResult {
  type: "employee" | "page";
  label: string;
  sublabel?: string;
  href: string;
}

const APP_PAGES: { label: string; href: string; keywords: string[] }[] = [
  { label: "Évaluation", href: "/evaluation", keywords: ["évaluation", "dashboard", "tableau de bord"] },
  { label: "Démarrer", href: "/intro", keywords: ["démarrer", "intro", "accueil", "start"] },
  { label: "Rencontres employés", href: "/rencontres-employes", keywords: ["rencontres", "meetings", "réunions"] },
  { label: "Employés", href: "/employes", keywords: ["employés", "liste", "répertoire", "employees"] },
  { label: "Objectifs", href: "/objectifs", keywords: ["objectifs", "objectives", "goals", "okr"] },
  { label: "Questionnaire", href: "/questionnaire", keywords: ["questionnaire", "sondage", "survey"] },
  { label: "Portail employé", href: "/employee-portal", keywords: ["portail", "portal", "mon espace"] },
  { label: "Mon développement", href: "/employee-portal/development", keywords: ["développement", "development", "croissance"] },
  { label: "Compétences (Rapports)", href: "/rapports/competences", keywords: ["compétences", "competencies", "rapport"] },
  { label: "Analyse talents", href: "/rapports/analyse-talents", keywords: ["talents", "analyse", "9 box", "nine box"] },
  { label: "Calibration (Rapports)", href: "/rapports/calibration", keywords: ["calibration", "gestionnaire", "sévérité", "notation"] },
  { label: "Modèles", href: "/administration/modeles", keywords: ["modèles", "templates", "gabarits"] },
  { label: "Profils de compétences", href: "/administration/profils-competences", keywords: ["profils", "profiles", "compétences"] },
  { label: "Compétences (Admin)", href: "/administration/competences", keywords: ["compétences", "admin", "gestion"] },
  { label: "Vision", href: "/administration/vision", keywords: ["vision", "valeurs", "values"] },
  { label: "Cycles d'évaluation", href: "/administration/evaluation-cycles", keywords: ["cycles", "évaluation", "evaluation"] },
];

export default function SidebarSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const searchEmployees = useCallback(async (q: string): Promise<SearchResult[]> => {
    const employees = await searchEmployeesByQuery(q, 5);
    return employees.map((emp) => ({
      type: "employee" as const,
      label: `${emp.first_name} ${emp.last_name}`,
      sublabel: emp.role_title || undefined,
      href: `/employee-profile?employeeId=${emp.id}`,
    }));
  }, []);

  const searchPages = useCallback((q: string): SearchResult[] => {
    const lower = q.toLowerCase();
    return APP_PAGES.filter(
      (p) =>
        p.label.toLowerCase().includes(lower) ||
        p.keywords.some((k) => k.includes(lower))
    )
      .slice(0, 5)
      .map((p) => ({
        type: "page" as const,
        label: p.label,
        href: p.href,
      }));
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const [employees, pages] = await Promise.all([
        searchEmployees(query.trim()),
        Promise.resolve(searchPages(query.trim())),
      ]);
      setResults([...pages, ...employees]);
      setSelectedIndex(0);
      setIsOpen(true);
      setIsLoading(false);
    }, 250);

    return () => clearTimeout(debounceRef.current);
  }, [query, searchEmployees, searchPages]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (result: SearchResult) => {
    navigate(result.href);
    setQuery("");
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative px-5 mb-6 transition-opacity duration-300">
      <div className="flex items-center gap-2 px-3.5 py-1.5 bg-muted rounded-lg">
        {isLoading ? (
          <Loader2 className="w-4 h-4 text-muted-foreground flex-shrink-0 animate-spin" />
        ) : (
          <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        )}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.trim() && results.length > 0 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t("common.search")}
          className="bg-transparent border-none outline-none text-sm text-muted-foreground w-full"
        />
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute left-5 right-5 top-full mt-1 bg-white rounded-xl shadow-lg border border-border overflow-hidden z-50 max-h-[320px] overflow-y-auto">
          {/* Pages */}
          {results.some((r) => r.type === "page") && (
            <div>
              <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                Pages
              </div>
              {results
                .filter((r) => r.type === "page")
                .map((result) => {
                  const globalIndex = results.indexOf(result);
                  return (
                    <button
                      key={result.href}
                      onClick={() => handleSelect(result)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                        globalIndex === selectedIndex
                          ? "bg-muted text-foreground"
                          : "text-muted-foreground hover:bg-muted/50"
                      }`}
                    >
                      <FileText className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
                      <span className="truncate">{result.label}</span>
                    </button>
                  );
                })}
            </div>
          )}

          {/* Employees */}
          {results.some((r) => r.type === "employee") && (
            <div>
              <div className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-t border-border/50">
                Employés
              </div>
              {results
                .filter((r) => r.type === "employee")
                .map((result) => {
                  const globalIndex = results.indexOf(result);
                  return (
                    <button
                      key={result.href}
                      onClick={() => handleSelect(result)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                        globalIndex === selectedIndex
                          ? "bg-muted text-foreground"
                          : "text-muted-foreground hover:bg-muted/50"
                      }`}
                    >
                      <Users className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
                      <div className="min-w-0">
                        <div className="truncate">{result.label}</div>
                        {result.sublabel && (
                          <div className="text-xs text-muted-foreground truncate">{result.sublabel}</div>
                        )}
                      </div>
                    </button>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {isOpen && query.trim().length >= 2 && results.length === 0 && !isLoading && (
        <div className="absolute left-5 right-5 top-full mt-1 bg-white rounded-xl shadow-lg border border-border z-50 p-4 text-center text-sm text-muted-foreground">
          Aucun résultat pour « {query} »
        </div>
      )}
    </div>
  );
}
